import { inject, Injectable } from "@angular/core";
import { DataService as CoreDataService, PersistMode, Query, QueryObjectsModel} from "@barthauer/abc/core/data";
import { DataService, ObjectKeys } from "../../../services/data.service";
import { concatMap, first, forkJoin, map, Observable, of, throwError } from "rxjs";
import { Address, AddressPerson } from "@barthauer/custom/xmt-models";
import { PersonStatus, ROLES } from "../../../models/enumeration-list.model";
import { ProjectPersonViewModel } from "../../../models/ProjectPerson.viewModel";
@Injectable()
export class CompanyRoleService{
    private readonly coreDataService=inject(CoreDataService);
    private readonly dataService=inject(DataService);

    static readonly  NO_PERSON_FOUND_ERROR = "Person not found";
    public getCompaniesData():Observable<Partial<Address>[]> {
    const columns=[
        this.coreDataService.createQueryColumn('Id', 'Id'),
        this.coreDataService.createQueryColumn('Name', 'Name'),
    ]
    return this.dataService.readObjects(ObjectKeys.ADDRESS,columns) as unknown as Observable<Partial<Address>[]>;
    }
    public getCompaniesByPersonId(id:string):Observable<Partial<AddressPerson>[]> {
        const personQuery: Query = new Query();
        personQuery.ObjectType = ObjectKeys.PERSON;
        personQuery.Columns = [
            this.coreDataService.createQueryColumn("Id","Id")
        ];
        personQuery.OPath=`Id='${id}'`;
        const AddressPersonQuery: Query = new Query();
        AddressPersonQuery.Name = 'AddressPerson';
        AddressPersonQuery.OPath = 'AddressPerson_IdRefPerson_Person';
        AddressPersonQuery.Columns = [
            this.coreDataService.createQueryColumn("Id","Id"),
            this.coreDataService.createQueryColumn("Position","Position"),
            this.coreDataService.createQueryColumn("ParentId","ParentId"),
            this.coreDataService.createQueryColumn("Address.Name","Name"),
            this.coreDataService.createQueryColumn("Status"),
        ];
        personQuery.ObjectQueries = [AddressPersonQuery];
        const mainQuery: QueryObjectsModel = new QueryObjectsModel();
        mainQuery.ObjectQueries = [personQuery];
        return this.coreDataService.executeReadObjectsQuery(mainQuery).pipe(map((result)=>(Array.isArray(result) && result.length>0 && Array.isArray(result[0]['AddressPerson'])) ? result[0]['AddressPerson'] : [])) as unknown as Observable<Partial<AddressPerson>[]>;
    }
    public persistAssociatedCompanies(
        personId:string,
        newCompanies:Partial<AddressPerson>[],
        updatedCompanies:Partial<AddressPerson>[],
        deletedCompanies:Partial<AddressPerson>[]
    ):Observable<ArrayBuffer>{
        if(personId){
            const query=[
                this.prepareInsertQuery(personId,newCompanies),
                this.prepareUpdateQuery(updatedCompanies),
                this.prepareDeleteQuery(deletedCompanies),
            ].filter(q => q);
            return forkJoin([
                this.dataService.persistMultipleObjects(query),
                this.getProjectsIdsByCompany(personId,[...newCompanies,...updatedCompanies].filter((company)=>company.Position===ROLES.Admin).map((company)=>`'${company.ParentId}'`))
            ]).pipe(
                first(),
                concatMap(result=>{
                    const projectPersonQuery= this.prepareInsertProjectPersonQuery(personId,result[1]);
                    return projectPersonQuery 
                    ? this.dataService.persistMultipleObjects([projectPersonQuery]).pipe(
                        map(()=>result[0])
                    )
                    : of(result[0]);
                })
            );
        }
        return throwError(() => CompanyRoleService.NO_PERSON_FOUND_ERROR);
    }
    private prepareInsertQuery(personId:string,newCompanies:Partial<AddressPerson>[]):{ 
        objectKey: ObjectKeys; 
        persistMode: PersistMode; 
        data: Partial<AddressPerson>[] 
    }{
        if(newCompanies.length===0) return null;
        const objectKey=ObjectKeys.ADDRESSPERSON;
        const addressPersonData = [];
        newCompanies.forEach(company=>{
            addressPersonData.push(
                {
                    Position: company.Position,
                    ParentId:company.ParentId,
                    IdRefPerson: personId,
                }
            )
        });
        return { objectKey: objectKey, persistMode: PersistMode.Insert, data: addressPersonData };
    }
    private prepareUpdateQuery(updatedCompanies:Partial<AddressPerson>[]):{ 
        objectKey: ObjectKeys; 
        persistMode: PersistMode; 
        data: Partial<AddressPerson>[] 
    }{
        if(updatedCompanies.length===0) return null;
        const objectKey=ObjectKeys.ADDRESSPERSON;
        const addressPersonData = [];
        updatedCompanies.forEach(company=>{
            addressPersonData.push(
                {
                    Id: company.Id,
                    Position: company.Position
                }
            )
        });
        return { objectKey: objectKey, persistMode: PersistMode.Update, data: addressPersonData };
    }
    private prepareDeleteQuery(deletedCompanies:Partial<AddressPerson>[]):{ 
        objectKey: ObjectKeys; 
        persistMode: PersistMode; 
        data: Partial<AddressPerson>[] 
    }{
        if(deletedCompanies.length===0) return null;
        const objectKey=ObjectKeys.ADDRESSPERSON;
        const addressPersonData = [];
        deletedCompanies.forEach(company=>{
            addressPersonData.push(
                {
                    Id: company.Id,
                    Status: PersonStatus.ARCHIVED
                }
            )
        });
        return { objectKey: objectKey, persistMode: PersistMode.Update, data: addressPersonData };
    }
    private prepareInsertProjectPersonQuery(personId:string,projectIds:string[]):{ 
        objectKey: ObjectKeys; 
        persistMode: PersistMode; 
        data: Partial<ProjectPersonViewModel>[] 
    } | null {
        if(projectIds.length===0) return null;
        const objectKey=ObjectKeys.PROJECT_PERSON;
        const projectPersonData: Partial<ProjectPersonViewModel>[] = [];
        projectIds.forEach(projectId=>{
            projectPersonData.push(
                {
                    ParentId:projectId,
                    IdRefPerson: personId,
                }
            )
        });
        return { objectKey: objectKey, persistMode: PersistMode.Insert, data: projectPersonData };
    }
    private getProjectsIdsByCompany(personId:string,companiesId:string[]):Observable<string[]>{
        return companiesId.length>0 ? this.dataService.readObjects(ObjectKeys.PROJECT,[this.coreDataService.createQueryColumn('Id')],`ParentId IN (${companiesId.join(',')}) AND Not(Exists(MemberPersons[IdRefPerson='${personId}']))`).pipe(
            map(result=>Array.isArray(result) ? result.map(project=>project.Id) : [])
        ): of([]);
    }

}